// =============================================================================
// MeuDriver — Admin User Detail (Batch J3)
// =============================================================================

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ChevronLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Star,
  BookOpen,
  DollarSign,
  UserX,
  UserCheck,
  UserCog,
  ShieldAlert,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { formatDate, formatCurrency, getInitials } from '@/lib/utils'
import {
  useAdminUserDetail,
  useAdminBanUser,
  useAdminUnbanUser,
  useAdminChangeRole,
  type UserRole,
} from '@/hooks/useAdminUsers'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roleBadge(role: UserRole) {
  switch (role) {
    case 'admin':
      return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Admin</Badge>
    case 'instructor':
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Instrutor</Badge>
    default:
      return <Badge variant="secondary">Aluno</Badge>
  }
}

function bookingStatusLabel(status: string): string {
  const map: Record<string, string> = {
    pending_payment:         'Aguardando pagamento',
    confirmed:               'Confirmada',
    in_progress:             'Em andamento',
    completed:               'Concluída',
    cancelled_by_student:    'Cancelada pelo aluno',
    cancelled_by_instructor: 'Cancelada pelo instrutor',
    no_show:                 'Não compareceu',
  }
  return map[status] ?? status
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const [confirmBan,     setConfirmBan]     = useState(false)
  const [confirmUnban,   setConfirmUnban]   = useState(false)
  const [roleDialog,     setRoleDialog]     = useState(false)
  const [selectedRole,   setSelectedRole]   = useState<UserRole | ''>('')

  const { data, isLoading, error } = useAdminUserDetail(userId ?? '')
  const banUser    = useAdminBanUser()
  const unbanUser  = useAdminUnbanUser()
  const changeRole = useAdminChangeRole()

  if (!userId) {
    return <p className="text-sm text-muted-foreground p-6">ID de usuário inválido.</p>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-60 w-full rounded-xl" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-destructive">{error?.message ?? 'Usuário não encontrado.'}</p>
        <Button variant="outline" size="sm" className="mt-4" asChild>
          <Link to="/admin/usuarios">Voltar</Link>
        </Button>
      </div>
    )
  }

  const { profile, bookings, stats } = data
  const isBanned = profile.status === 'banned'

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" asChild>
          <Link to="/admin/usuarios">
            <ChevronLeft className="h-4 w-4" />
            Usuários
          </Link>
        </Button>
      </div>

      {/* Perfil */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <Avatar className="h-16 w-16 ring-2 ring-border shrink-0">
              <AvatarImage src={profile.avatar_url ?? undefined} />
              <AvatarFallback className="text-lg font-bold bg-brand-100 text-brand-700">
                {getInitials(profile.full_name ?? 'U')}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1 className="text-xl font-bold">{profile.full_name ?? 'Sem nome'}</h1>
                {roleBadge(profile.role)}
                {profile.status === 'banned' && (
                  <Badge variant="destructive">Banido</Badge>
                )}
                {profile.status === 'suspended' && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">Suspenso</Badge>
                )}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {profile.email && (
                  <span className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />{profile.email}
                  </span>
                )}
                {profile.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />{profile.phone}
                  </span>
                )}
                {(profile.city || profile.state) && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {[profile.city, profile.state].filter(Boolean).join(', ')}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Cadastro em {formatDate(profile.created_at)}
                </span>
              </div>

              {/* Ações */}
              <div className="flex flex-wrap gap-2 mt-4">
                {isBanned ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => setConfirmUnban(true)}
                    data-testid="btn-unban-user"
                  >
                    <UserCheck className="h-4 w-4" />Desbanir
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => setConfirmBan(true)}
                    data-testid="btn-ban-user"
                  >
                    <UserX className="h-4 w-4" />Banir
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => { setSelectedRole(profile.role); setRoleDialog(true) }}
                  data-testid="btn-change-role"
                >
                  <UserCog className="h-4 w-4" />Mudar role
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Métricas */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
                <BookOpen className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_bookings_student}</p>
                <p className="text-xs text-muted-foreground">Reservas como aluno</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100">
                <User className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_bookings_instructor}</p>
                <p className="text-xs text-muted-foreground">Aulas como instrutor</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
                <Star className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats.avg_rating != null ? Number(stats.avg_rating).toFixed(1) : '—'}
                </p>
                <p className="text-xs text-muted-foreground">Avaliação média (instrutor)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_revenue)}</p>
                <p className="text-xs text-muted-foreground">Ganhos como instrutor</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings recentes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Bookings recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Nenhum booking encontrado.</p>
          ) : (
            <div className="divide-y divide-border">
              {bookings.map((b) => (
                <div key={b.id} className="flex items-center justify-between py-3 gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {bookingStatusLabel(b.status)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(b.scheduled_at).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {b.total_price != null && (
                    <span className="text-sm font-semibold text-emerald-700">
                      {formatCurrency(b.total_price)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: banir */}
      <Dialog open={confirmBan} onOpenChange={setConfirmBan}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Banir usuário
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja banir <strong>{profile.full_name ?? 'este usuário'}</strong>?
              O acesso sera removido imediatamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBan(false)} disabled={banUser.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={banUser.isPending}
              data-testid="btn-confirm-ban"
              onClick={() =>
                banUser.mutate(userId, {
                  onSuccess: () => { toast.success('Usuário banido.'); setConfirmBan(false) },
                  onError:   (err) => { toast.error(err.message); setConfirmBan(false) },
                })
              }
            >
              {banUser.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Banir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: desbanir */}
      <Dialog open={confirmUnban} onOpenChange={setConfirmUnban}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desbanir usuário</DialogTitle>
            <DialogDescription>
              Deseja restaurar o acesso de <strong>{profile.full_name ?? 'este usuário'}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUnban(false)} disabled={unbanUser.isPending}>
              Cancelar
            </Button>
            <Button
              disabled={unbanUser.isPending}
              data-testid="btn-confirm-unban"
              onClick={() =>
                unbanUser.mutate(userId, {
                  onSuccess: () => { toast.success('Usuário desbanido.'); setConfirmUnban(false) },
                  onError:   (err) => { toast.error(err.message); setConfirmUnban(false) },
                })
              }
            >
              {unbanUser.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Desbanir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: mudar role */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar role</DialogTitle>
            <DialogDescription>
              Mude a role de <strong>{profile.full_name ?? 'este usuário'}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={selectedRole || profile.role} onValueChange={(v) => setSelectedRole(v as UserRole)}>
              <SelectTrigger data-testid="select-new-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Aluno</SelectItem>
                <SelectItem value="instructor">Instrutor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)} disabled={changeRole.isPending}>
              Cancelar
            </Button>
            <Button
              disabled={changeRole.isPending || !selectedRole || selectedRole === profile.role}
              data-testid="btn-confirm-role"
              onClick={() => {
                if (!selectedRole) return
                changeRole.mutate({ userId, role: selectedRole as UserRole }, {
                  onSuccess: () => { toast.success('Role atualizada.'); setRoleDialog(false) },
                  onError:   (err) => { toast.error(err.message); setRoleDialog(false) },
                })
              }}
            >
              {changeRole.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
